/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Verdi Dashboard API
 * API behind the Verdi irrigation dashboard: organizations, their devices, zones and irrigation schedules, and the sensor data those devices report.
 *
 * ## Authentication
 *
 * Most operations need a Descope session token, sent as `Authorization: Bearer <token>`. Each operation's `security` lists what it accepts. Role and permission names there, such as `Verdi Administrator`, are Descope roles or permissions the session must carry, and the operation's description repeats them under **Access**. Operations with `security: []` are public.
 *
 * ## Organization context
 *
 * Data belongs to organizations. A user's session acts as their primary organization, or as another organization they belong to when its ID is sent in the `User` header. Administrators must send the `User` header on organization-scoped operations, or the request is rejected with a 400.
 *
 * ## Errors
 *
 * Error bodies are JSON `{ "message": string }`.
 *
 * - **400**: the path, query or body failed validation, or the request broke a domain rule.
 * - **401 / 403**: sent by the auth middleware; the body may be empty.
 * - **409**: see below.
 * - **500**: an unexpected server error.
 *
 * ## Versioned resources
 *
 * Versioned resources carry a `versionId`. Send the `versionId` you last read on a PATCH (in the body) or a DELETE (as a query parameter). If the resource changed since, the request gets a 409: reload it and retry. A 409 that includes a `code` is a conflict specific to that operation, described on the operation. On a create, 409 means the resource already exists.
 *
 * ## Reads with a request body
 *
 * Some reads take their input as a JSON body on the HTTP `QUERY` method; these operations are marked `x-read-only: true`. Deprecated POST and GET variants serve clients that cannot send `QUERY`.
 *
 * ## Identifiers
 *
 * MongoDB ObjectIds are 24-character hexadecimal strings.
 * OpenAPI spec version: 1.0.0
 */

import type {
	DataTag,
	DefinedInitialDataOptions,
	DefinedUseQueryResult,
	MutationFunction,
	QueryClient,
	QueryFunction,
	QueryKey,
	UndefinedInitialDataOptions,
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "../custom-fetch.ts";
import type {
	AssetIdentity,
	AssetIdentityWithOwner,
	CreateAssetIdentity400,
	CreateAssetIdentity429,
	CreateAssetIdentity500,
	CreateAssetIdentityBody,
	GetAssetIdentity400,
	GetAssetIdentity404,
	GetAssetIdentity500,
	GetAssetIdentityByLoraEui400,
	GetAssetIdentityByLoraEui404,
	GetAssetIdentityByLoraEui500,
	GetAssetOwner400,
	GetAssetOwner403,
	GetAssetOwner404,
	GetAssetOwner500,
} from "./schemas";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
	query: T,
	queryKey: K,
): T & { queryKey: K } => {
	const result = { queryKey } as T & { queryKey: K };
	for (const key of Object.keys(query)) {
		// The explicit queryKey always wins, matching the previous
		// `{ ...query, queryKey }` spread where it was set last.
		if (key === "queryKey") continue;
		Object.defineProperty(result, key, {
			enumerable: true,
			configurable: true,
			get: () => (query as Record<string, unknown>)[key],
		});
	}
	return result;
};

export const getGetAssetIdentityByLoraEuiUrl = (loraEui: string) => {
	return `/asset-identities/by-lora-eui/${loraEui}`;
};

/**
 * **Access:** Requires a user session token.
 * @summary Look up an asset identity by its LoRa EUI
 */
export const getAssetIdentityByLoraEui = async (
	loraEui: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<AssetIdentity> => {
	return customFetch<AssetIdentity>(getGetAssetIdentityByLoraEuiUrl(loraEui), {
		...options,
		method: "GET",
	});
};

export const getGetAssetIdentityByLoraEuiQueryKey = (loraEui: string) => {
	return ["asset-identities", "by-lora-eui", loraEui] as const;
};

export const getGetAssetIdentityByLoraEuiQueryOptions = <
	TData = Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
	TError =
		| GetAssetIdentityByLoraEui400
		| void
		| GetAssetIdentityByLoraEui404
		| GetAssetIdentityByLoraEui500,
>(
	loraEui: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey =
		queryOptions?.queryKey ?? getGetAssetIdentityByLoraEuiQueryKey(loraEui);

	const queryFn: QueryFunction<
		Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>
	> = ({ signal }) =>
		getAssetIdentityByLoraEui(loraEui, { signal, ...requestOptions });

	return {
		queryKey,
		queryFn,
		enabled: loraEui !== null && loraEui !== undefined,
		...queryOptions,
	} as UseQueryOptions<
		Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAssetIdentityByLoraEuiQueryResult = NonNullable<
	Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>
>;
export type GetAssetIdentityByLoraEuiQueryError =
	| GetAssetIdentityByLoraEui400
	| void
	| GetAssetIdentityByLoraEui404
	| GetAssetIdentityByLoraEui500;

export function useGetAssetIdentityByLoraEui<
	TData = Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
	TError =
		| GetAssetIdentityByLoraEui400
		| void
		| GetAssetIdentityByLoraEui404
		| GetAssetIdentityByLoraEui500,
>(
	loraEui: string,
	options: {
		query: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
				TError,
				TData
			>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
					TError,
					Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAssetIdentityByLoraEui<
	TData = Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
	TError =
		| GetAssetIdentityByLoraEui400
		| void
		| GetAssetIdentityByLoraEui404
		| GetAssetIdentityByLoraEui500,
>(
	loraEui: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
				TError,
				TData
			>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
					TError,
					Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAssetIdentityByLoraEui<
	TData = Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
	TError =
		| GetAssetIdentityByLoraEui400
		| void
		| GetAssetIdentityByLoraEui404
		| GetAssetIdentityByLoraEui500,
>(
	loraEui: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Look up an asset identity by its LoRa EUI
 */

export function useGetAssetIdentityByLoraEui<
	TData = Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
	TError =
		| GetAssetIdentityByLoraEui400
		| void
		| GetAssetIdentityByLoraEui404
		| GetAssetIdentityByLoraEui500,
>(
	loraEui: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getGetAssetIdentityByLoraEuiQueryOptions(
		loraEui,
		options,
	);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Look up an asset identity by its LoRa EUI
 */
export const useSetGetAssetIdentityByLoraEuiQueryData = () => {
	const queryClient = useQueryClient();
	return (
		loraEui: string,
		updater:
			| Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>
			| undefined
			| ((
					old:
						| Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>
						| undefined,
			  ) => Awaited<ReturnType<typeof getAssetIdentityByLoraEui>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<
			Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>
		>(
			{
				exact: $exactMatch,
				queryKey: getGetAssetIdentityByLoraEuiQueryKey(loraEui),
			},
			updater,
		);
	};
};

/**
 * @summary Look up an asset identity by its LoRa EUI
 */
export const useGetGetAssetIdentityByLoraEuiQueryData = () => {
	const queryClient = useQueryClient();
	return (loraEui: string) =>
		queryClient.getQueryData<
			Awaited<ReturnType<typeof getAssetIdentityByLoraEui>>
		>(getGetAssetIdentityByLoraEuiQueryKey(loraEui));
};

export const getGetAssetIdentityUrl = (verdiId: string) => {
	return `/asset-identities/by-verdi-id/${verdiId}`;
};

/**
 * **Access:** Requires a user session token.
 * @summary Look up an asset identity by its verdiId
 */
export const getAssetIdentity = async (
	verdiId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<AssetIdentity> => {
	return customFetch<AssetIdentity>(getGetAssetIdentityUrl(verdiId), {
		...options,
		method: "GET",
	});
};

export const getGetAssetIdentityQueryKey = (verdiId: string) => {
	return ["asset-identities", "by-verdi-id", verdiId] as const;
};

export const getGetAssetIdentityQueryOptions = <
	TData = Awaited<ReturnType<typeof getAssetIdentity>>,
	TError =
		| GetAssetIdentity400
		| void
		| GetAssetIdentity404
		| GetAssetIdentity500,
>(
	verdiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getAssetIdentity>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey =
		queryOptions?.queryKey ?? getGetAssetIdentityQueryKey(verdiId);

	const queryFn: QueryFunction<
		Awaited<ReturnType<typeof getAssetIdentity>>
	> = ({ signal }) => getAssetIdentity(verdiId, { signal, ...requestOptions });

	return {
		queryKey,
		queryFn,
		enabled: verdiId !== null && verdiId !== undefined,
		...queryOptions,
	} as UseQueryOptions<
		Awaited<ReturnType<typeof getAssetIdentity>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAssetIdentityQueryResult = NonNullable<
	Awaited<ReturnType<typeof getAssetIdentity>>
>;
export type GetAssetIdentityQueryError =
	| GetAssetIdentity400
	| void
	| GetAssetIdentity404
	| GetAssetIdentity500;

export function useGetAssetIdentity<
	TData = Awaited<ReturnType<typeof getAssetIdentity>>,
	TError =
		| GetAssetIdentity400
		| void
		| GetAssetIdentity404
		| GetAssetIdentity500,
>(
	verdiId: string,
	options: {
		query: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getAssetIdentity>>,
				TError,
				TData
			>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof getAssetIdentity>>,
					TError,
					Awaited<ReturnType<typeof getAssetIdentity>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAssetIdentity<
	TData = Awaited<ReturnType<typeof getAssetIdentity>>,
	TError =
		| GetAssetIdentity400
		| void
		| GetAssetIdentity404
		| GetAssetIdentity500,
>(
	verdiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getAssetIdentity>>,
				TError,
				TData
			>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof getAssetIdentity>>,
					TError,
					Awaited<ReturnType<typeof getAssetIdentity>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAssetIdentity<
	TData = Awaited<ReturnType<typeof getAssetIdentity>>,
	TError =
		| GetAssetIdentity400
		| void
		| GetAssetIdentity404
		| GetAssetIdentity500,
>(
	verdiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getAssetIdentity>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Look up an asset identity by its verdiId
 */

export function useGetAssetIdentity<
	TData = Awaited<ReturnType<typeof getAssetIdentity>>,
	TError =
		| GetAssetIdentity400
		| void
		| GetAssetIdentity404
		| GetAssetIdentity500,
>(
	verdiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getAssetIdentity>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getGetAssetIdentityQueryOptions(verdiId, options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Look up an asset identity by its verdiId
 */
export const useSetGetAssetIdentityQueryData = () => {
	const queryClient = useQueryClient();
	return (
		verdiId: string,
		updater:
			| Awaited<ReturnType<typeof getAssetIdentity>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof getAssetIdentity>> | undefined,
			  ) => Awaited<ReturnType<typeof getAssetIdentity>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof getAssetIdentity>>>(
			{ exact: $exactMatch, queryKey: getGetAssetIdentityQueryKey(verdiId) },
			updater,
		);
	};
};

/**
 * @summary Look up an asset identity by its verdiId
 */
export const useGetGetAssetIdentityQueryData = () => {
	const queryClient = useQueryClient();
	return (verdiId: string) =>
		queryClient.getQueryData<Awaited<ReturnType<typeof getAssetIdentity>>>(
			getGetAssetIdentityQueryKey(verdiId),
		);
};

export const getGetAssetOwnerUrl = (verdiId: string) => {
	return `/asset-identities/by-verdi-id/${verdiId}/owner`;
};

/**
 * **Access:** Requires a user session token.
 * @summary Look up an asset identity by its verdiId, with the organization that owns the asset
 */
export const getAssetOwner = async (
	verdiId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<AssetIdentityWithOwner> => {
	return customFetch<AssetIdentityWithOwner>(getGetAssetOwnerUrl(verdiId), {
		...options,
		method: "GET",
	});
};

export const getGetAssetOwnerQueryKey = (verdiId: string) => {
	return ["asset-identities", "by-verdi-id", verdiId, "owner"] as const;
};

export const getGetAssetOwnerQueryOptions = <
	TData = Awaited<ReturnType<typeof getAssetOwner>>,
	TError =
		| GetAssetOwner400
		| void
		| GetAssetOwner403
		| GetAssetOwner404
		| GetAssetOwner500,
>(
	verdiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getAssetOwner>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getGetAssetOwnerQueryKey(verdiId);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof getAssetOwner>>> = ({
		signal,
	}) => getAssetOwner(verdiId, { signal, ...requestOptions });

	return {
		queryKey,
		queryFn,
		enabled: verdiId !== null && verdiId !== undefined,
		...queryOptions,
	} as UseQueryOptions<
		Awaited<ReturnType<typeof getAssetOwner>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAssetOwnerQueryResult = NonNullable<
	Awaited<ReturnType<typeof getAssetOwner>>
>;
export type GetAssetOwnerQueryError =
	| GetAssetOwner400
	| void
	| GetAssetOwner403
	| GetAssetOwner404
	| GetAssetOwner500;

export function useGetAssetOwner<
	TData = Awaited<ReturnType<typeof getAssetOwner>>,
	TError =
		| GetAssetOwner400
		| void
		| GetAssetOwner403
		| GetAssetOwner404
		| GetAssetOwner500,
>(
	verdiId: string,
	options: {
		query: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getAssetOwner>>, TError, TData>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof getAssetOwner>>,
					TError,
					Awaited<ReturnType<typeof getAssetOwner>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAssetOwner<
	TData = Awaited<ReturnType<typeof getAssetOwner>>,
	TError =
		| GetAssetOwner400
		| void
		| GetAssetOwner403
		| GetAssetOwner404
		| GetAssetOwner500,
>(
	verdiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getAssetOwner>>, TError, TData>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof getAssetOwner>>,
					TError,
					Awaited<ReturnType<typeof getAssetOwner>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAssetOwner<
	TData = Awaited<ReturnType<typeof getAssetOwner>>,
	TError =
		| GetAssetOwner400
		| void
		| GetAssetOwner403
		| GetAssetOwner404
		| GetAssetOwner500,
>(
	verdiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getAssetOwner>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Look up an asset identity by its verdiId, with the organization that owns the asset
 */

export function useGetAssetOwner<
	TData = Awaited<ReturnType<typeof getAssetOwner>>,
	TError =
		| GetAssetOwner400
		| void
		| GetAssetOwner403
		| GetAssetOwner404
		| GetAssetOwner500,
>(
	verdiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getAssetOwner>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getGetAssetOwnerQueryOptions(verdiId, options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Look up an asset identity by its verdiId, with the organization that owns the asset
 */
export const useSetGetAssetOwnerQueryData = () => {
	const queryClient = useQueryClient();
	return (
		verdiId: string,
		updater:
			| Awaited<ReturnType<typeof getAssetOwner>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof getAssetOwner>> | undefined,
			  ) => Awaited<ReturnType<typeof getAssetOwner>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof getAssetOwner>>>(
			{ exact: $exactMatch, queryKey: getGetAssetOwnerQueryKey(verdiId) },
			updater,
		);
	};
};

/**
 * @summary Look up an asset identity by its verdiId, with the organization that owns the asset
 */
export const useGetGetAssetOwnerQueryData = () => {
	const queryClient = useQueryClient();
	return (verdiId: string) =>
		queryClient.getQueryData<Awaited<ReturnType<typeof getAssetOwner>>>(
			getGetAssetOwnerQueryKey(verdiId),
		);
};

export const getCreateAssetIdentityUrl = () => {
	return `/asset-identities`;
};

/**
 * **Access:** Requires a production jig access key or an administrator session.
 * @summary Mint an asset identity for a LoRa EUI, or return the identity it already has
 */
export const createAssetIdentity = async (
	createAssetIdentityBody: CreateAssetIdentityBody,
	options?: Parameters<typeof customFetch>[1],
): Promise<AssetIdentity> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<AssetIdentity>(getCreateAssetIdentityUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(createAssetIdentityBody),
	});
};

export const getCreateAssetIdentityMutationKey = () =>
	["createAssetIdentity"] as const;

export const getCreateAssetIdentityMutationOptions = <
	TError =
		| CreateAssetIdentity400
		| void
		| CreateAssetIdentity429
		| CreateAssetIdentity500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof createAssetIdentity>>,
		TError,
		CreateAssetIdentityMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof createAssetIdentity>>,
	TError,
	CreateAssetIdentityMutationVariables,
	TContext
> => {
	const mutationKey = getCreateAssetIdentityMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof createAssetIdentity>>,
		CreateAssetIdentityMutationVariables
	> = (props) => {
		const { data } = props ?? {};

		return createAssetIdentity(data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type CreateAssetIdentityMutationResult = NonNullable<
	Awaited<ReturnType<typeof createAssetIdentity>>
>;
export type CreateAssetIdentityMutationBody = CreateAssetIdentityBody;
export type CreateAssetIdentityMutationError =
	| CreateAssetIdentity400
	| void
	| CreateAssetIdentity429
	| CreateAssetIdentity500;
export type CreateAssetIdentityMutationVariables = {
	data: CreateAssetIdentityBody;
};

/**
 * @summary Mint an asset identity for a LoRa EUI, or return the identity it already has
 */
export const useCreateAssetIdentity = <
	TError =
		| CreateAssetIdentity400
		| void
		| CreateAssetIdentity429
		| CreateAssetIdentity500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof createAssetIdentity>>,
			TError,
			CreateAssetIdentityMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof createAssetIdentity>>,
	TError,
	CreateAssetIdentityMutationVariables,
	TContext
> => {
	return useMutation(
		getCreateAssetIdentityMutationOptions(options),
		queryClient,
	);
};
